/**
 * 清溪天成模版 - 详情页
 * 
 * 设计：全宽Hero图 + 居中内容 + 右侧悬浮联系卡
 * 独特排版：大型Hero区使用全视图，下方内容居中窄列排版，右侧带毛玻璃悬浮联系卡
 */

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/templates/crystal/components/Navbar";
import Footer from "@/templates/crystal/components/Footer";
import { getListingDetail, getListings } from "@/utils/api";

export default function CrystalListingDetail() {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<any>(null);
  const [related, setRelated] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getListingDetail(id)
      .then((data) => {
        setListing(data);
        const catId = data?.category_id;
        if (catId) {
          return getListings({ page: 1, pageSize: 4, categoryId: catId, sort: "created_at desc" });
        }
      })
      .then((res: any) => {
        const list = res?.list || res?.data?.list || [];
        setRelated(list.filter((r: any) => r.id !== id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSkeleton />;
  if (!listing) return <NotFound />;

  const images = listing.images || [];
  const hasImages = images.length > 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero图片区 */}
      <div className="relative bg-gray-900 h-[40vh] md:h-[50vh] min-h-[320px] overflow-hidden">
        {hasImages ? (
          <>
            <img src={images[activeImage]} alt={listing.title} className="w-full h-full object-cover" />
            {/* 图片切换 */}
            {images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {images.map((img: string, idx: number) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${idx === activeImage ? "bg-white w-6" : "bg-white/50 hover:bg-white/80"}`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-blue-600/20">
            <i className="ri-image-line text-6xl text-cyan-200/50" />
          </div>
        )}

        {/* 底部渐变 */}
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>

      {/* 内容区 */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 -mt-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* 主内容列 */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm">
              {/* 标签 */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-cyan-50 text-cyan-600 text-xs font-semibold rounded-full">
                  {listing.category_name || "信息"}
                </span>
                {listing.is_top === 1 && (
                  <span className="px-3 py-1 bg-red-50 text-red-500 text-xs font-semibold rounded-full">置顶</span>
                )}
                {listing.is_urgent === 1 && (
                  <span className="px-3 py-1 bg-orange-50 text-orange-500 text-xs font-semibold rounded-full">急</span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{listing.title}</h1>

              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mb-6 pb-6 border-b border-gray-100">
                <span className="flex items-center gap-1"><i className="ri-map-pin-line text-cyan-400" />{listing.city_name || listing.district || "未知位置"}</span>
                <span className="flex items-center gap-1"><i className="ri-eye-line" />{listing.views || 0} 次浏览</span>
                <span className="flex items-center gap-1"><i className="ri-time-line" />{listing.created_at ? new Date(listing.created_at).toLocaleDateString("zh-CN") : ""}</span>
                {listing.price && (
                  <span className="ml-auto text-xl font-bold text-cyan-600">¥{listing.price}</span>
                )}
              </div>

              {/* 内容 */}
              <div className="prose prose-gray max-w-none text-sm leading-relaxed">
                {listing.content || listing.description ? (
                  <div dangerouslySetInnerHTML={{ __html: listing.content || listing.description }} />
                ) : (
                  <p className="text-gray-400 text-center py-8">暂无详细描述</p>
                )}
              </div>

              {/* 图片缩略图 */}
              {hasImages && (
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">全部图片 ({images.length})</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((img: string, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`aspect-square rounded-xl overflow-hidden border-2 transition-all ${idx === activeImage ? "border-cyan-400 shadow-md" : "border-transparent hover:border-gray-200"}`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 右侧联系卡 */}
          <div className="lg:w-80 shrink-0">
            <div className="lg:sticky lg:top-24 space-y-4">
              {/* 发布者 */}
              <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-cyan-100/50 p-5 shadow-lg shadow-cyan-500/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold shadow-md shadow-cyan-400/25">
                    {(listing.nickname || listing.username || "用").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">{listing.nickname || listing.username || "用户"}</div>
                    <div className="text-xs text-gray-400">{listing.vip_expire_at && new Date(listing.vip_expire_at) > new Date() ? "VIP会员" : "普通会员"}</div>
                  </div>
                </div>

                {listing.phone && (
                  <a
                    href={`tel:${listing.phone}`}
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md shadow-cyan-500/20 text-sm mb-2"
                  >
                    <i className="ri-phone-line" /> {listing.phone}
                  </a>
                )}
                {listing.wechat && (
                  <div className="flex items-center justify-center gap-2 w-full py-2.5 bg-green-50 text-green-600 font-semibold rounded-xl text-sm">
                    <i className="ri-wechat-line" /> 微信: {listing.wechat}
                  </div>
                )}
                {listing.email && (
                  <div className="mt-2 text-center text-xs text-gray-400">
                    <i className="ri-mail-line mr-1" />{listing.email}
                  </div>
                )}
              </div>

              {/* 信息属性 */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">信息属性</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">浏览量</span>
                    <span className="text-gray-700 font-medium">{listing.views || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">发布时间</span>
                    <span className="text-gray-700">{listing.created_at ? new Date(listing.created_at).toLocaleDateString("zh-CN") : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">信息编号</span>
                    <span className="text-gray-700 font-mono text-xs">{id?.slice(0, 8)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="py-12 bg-cyan-50/20 mt-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-gradient-to-b from-cyan-400 to-blue-600" />
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-cyan-100/40 to-blue-50/40 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-2xl text-cyan-200" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-gray-800 group-hover:text-cyan-600 transition-colors line-clamp-1 text-sm">{item.title}</h3>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-gray-400">{item.city_name || ""}</span>
                      {item.price && <span className="text-cyan-600 font-bold">¥{item.price}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="h-[40vh] bg-gray-200 animate-pulse" />
      <div className="max-w-5xl mx-auto px-6 -mt-10">
        <div className="p-8 bg-white rounded-2xl border border-gray-100 animate-pulse space-y-4">
          <div className="h-4 bg-gray-100 rounded w-1/4" />
          <div className="h-8 bg-gray-100 rounded w-2/3" />
          <div className="h-4 bg-gray-100 rounded w-1/3" />
          <div className="space-y-2 pt-4">
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-full" />
            <div className="h-3 bg-gray-100 rounded w-2/3" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center px-4">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-cyan-50">
          <i className="ri-file-damage-line text-3xl text-cyan-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-800 mb-2">信息不存在</h1>
        <p className="text-sm text-gray-500 mb-6">该信息可能已被删除或过期</p>
        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-medium rounded-full hover:from-cyan-600 hover:to-blue-700 transition-colors">
          <i className="ri-home-4-line" />返回首页
        </Link>
      </div>
    </div>
  );
}
